import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Send, 
  Phone, 
  User,
  Clock,
  CheckCheck,
  Check,
  AlertCircle,
  FileText,
  MessageSquare,
  PlayCircle,
  RefreshCw,
  PhoneCall,
  ListTodo
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppCenter({ user, logout }) {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testName, setTestName] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchTemplates();
    fetchTasks();
    
    // Poll for new messages every 10 seconds
    const interval = setInterval(() => {
      fetchConversations();
      fetchTasks();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.customer_phone);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/conversations`, { withCredentials: true });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/tasks?status=pending`, { withCredentials: true });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Failed to load tasks");
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages/${phone}`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/templates`, { withCredentials: true });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to load templates");
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        phone_number: selectedConversation.customer_phone,
        country_code: "+91",
        message_text: messageText
      }, { withCredentials: true });
      
      toast.success("Message sent");
      setMessageText("");
      fetchMessages(selectedConversation.customer_phone);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const sendTemplateMessage = async () => {
    if (!selectedTemplate || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        phone_number: selectedConversation.customer_phone,
        country_code: "+91",
        template_name: selectedTemplate.name
      }, { withCredentials: true });
      
      toast.success("Template message sent");
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      fetchMessages(selectedConversation.customer_phone);
    } catch (error) {
      toast.error("Failed to send template");
    } finally {
      setSending(false);
    }
  };

  const testJourney = async () => {
    if (!testPhone) {
      toast.error("Please enter a phone number");
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(`${API}/whatsapp/test-journey`, {
        phone: testPhone,
        message: testMessage || "Hi",
        name: testName || "Test User"
      }, { withCredentials: true });
      
      toast.success(`Journey triggered: ${response.data.journey_result}`);
      fetchConversations();
    } catch (error) {
      toast.error("Failed to test journey");
    } finally {
      setSending(false);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.put(`${API}/whatsapp/tasks/${taskId}`, { status: "completed" }, { withCredentials: true });
      toast.success("Task completed");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <Check className="w-3 h-3 text-slate-400" />;
      case "delivered":
        return <CheckCheck className="w-3 h-3 text-slate-400" />;
      case "read":
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      case "failed":
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="h-[calc(100vh-140px)]" data-testid="whatsapp-center">
        <Tabs defaultValue="conversations" className="h-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="conversations" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Conversations
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-2">
                <ListTodo className="w-4 h-4" />
                Tasks {tasks.length > 0 && <Badge className="ml-1 bg-orange-500">{tasks.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="test" className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4" />
                Test Journey
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Conversations Tab */}
          <TabsContent value="conversations" className="h-[calc(100%-60px)]">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
              {/* Conversations List */}
              <Card className="lg:col-span-1 flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold font-[Manrope]">Conversations</CardTitle>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search conversations..." className="pl-10" />
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No conversations yet</p>
                        <p className="text-sm mt-1">Use "Test Journey" tab to simulate messages</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {conversations.map((conv, index) => (
                          <div
                            key={index}
                            className={`p-4 cursor-pointer transition-colors ${
                          selectedConversation?.customer_phone === conv.customer_phone
                            ? "bg-teal-50"
                            : "hover:bg-slate-50"
                        }`}
                        onClick={() => setSelectedConversation(conv)}
                        data-testid={`conversation-${conv.customer_phone}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-900 truncate">
                                {conv.customer_name || conv.customer_phone}
                              </p>
                              {conv.unread_count > 0 && (
                                <Badge className="bg-green-500 text-white text-xs">
                                  {conv.unread_count}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 truncate mt-1">{conv.last_message}</p>
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(conv.last_message_time)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-2 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <CardHeader className="pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {selectedConversation.customer_name || selectedConversation.customer_phone}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedConversation.customer_phone}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTemplateModal(true)}
                      data-testid="send-template-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Templates
                    </Button>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full p-4">
                    <div className="space-y-4">
                      {messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex flex-col ${msg.direction === "outbound" ? "items-end" : "items-start"}`}
                        >
                          {/* Label for User/Bot */}
                          <span className={`text-xs font-semibold mb-1 px-2 ${
                            msg.direction === "outbound" 
                              ? "text-green-700" 
                              : "text-slate-600"
                          }`}>
                            {msg.direction === "outbound" ? "🤖 HearClear Bot" : "👤 Customer"}
                          </span>
                          <div
                            className={
                              msg.direction === "outbound"
                                ? "chat-bubble-out"
                                : "chat-bubble-in"
                            }
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center gap-1 mt-1 ${
                              msg.direction === "outbound" ? "justify-end" : "justify-start"
                            }`}>
                              <span className={`text-xs ${
                                msg.direction === "outbound" ? "text-green-100" : "text-slate-400"
                              }`}>
                                {formatTime(msg.created_at)}
                              </span>
                              {msg.direction === "outbound" && getStatusIcon(msg.status)}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex gap-3">
                    <Textarea
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="flex-1 min-h-[44px] max-h-32 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      data-testid="message-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sending || !messageText.trim()}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-slate-700">Select a conversation</h3>
                  <p className="text-sm mt-1">Choose a conversation from the list to start messaging</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Template Modal */}
        <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Template Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {templates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No templates available</p>
                  <p className="text-sm mt-1">Create templates in your WhatsApp Business Manager</p>
                </div>
              ) : (
                <>
                  <Select onValueChange={(value) => setSelectedTemplate(templates.find(t => t.name === value))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.template_id} value={template.name}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedTemplate && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700">Preview:</p>
                      <p className="text-sm text-slate-600 mt-2">{selectedTemplate.body}</p>
                    </div>
                  )}

                  <Button
                    onClick={sendTemplateMessage}
                    disabled={!selectedTemplate || sending}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {sending ? "Sending..." : "Send Template"}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="h-[calc(100%-60px)]">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-[Manrope]">Pending Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pending tasks</p>
                    <p className="text-sm mt-1">Callback requests and follow-ups will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tasks.map((task) => (
                      <div key={task.task_id} className="p-4 rounded-xl bg-slate-50 border-l-4 border-l-orange-500">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              {task.type === "callback" ? (
                                <PhoneCall className="w-4 h-4 text-orange-500" />
                              ) : (
                                <MessageSquare className="w-4 h-4 text-blue-500" />
                              )}
                              <span className="font-medium capitalize">{task.type?.replace('_', ' ')}</span>
                              <Badge className={task.priority === "high" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                                {task.priority}
                              </Badge>
                            </div>
                            <p className="mt-2 text-slate-600">
                              <span className="font-medium">{task.customer_name || "Customer"}</span>
                              {task.phone && <span className="text-slate-500 ml-2">({task.phone})</span>}
                            </p>
                            {task.message && (
                              <p className="text-sm text-slate-500 mt-1 italic">"{task.message}"</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(task.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`tel:${task.phone}`, '_self')}
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => completeTask(task.task_id)}
                            >
                              Done
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Journey Tab */}
          <TabsContent value="test" className="h-[calc(100%-60px)]">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-[Manrope]">Test WhatsApp Journey</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-teal-50 rounded-xl">
                  <p className="text-sm text-cyan-800">
                    <PlayCircle className="w-4 h-4 inline mr-2" />
                    Simulate incoming WhatsApp messages to test the automated journey flows. 
                    Messages will be sent to the phone number via WhatsApp.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Phone Number *</label>
                    <Input
                      placeholder="9876543210"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      data-testid="test-phone-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Customer Name</label>
                    <Input
                      placeholder="Test Customer"
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label">Simulated Message</label>
                    <Input
                      placeholder="Hi"
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={testJourney}
                    disabled={sending || !testPhone}
                    className="bg-cyan-700 hover:bg-cyan-800"
                    data-testid="test-journey-btn"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Trigger Journey
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={fetchConversations}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-medium text-slate-900 mb-4">Quick Test Messages</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { msg: "Hi", label: "Welcome" },
                      { msg: "1", label: "Products" },
                      { msg: "2", label: "Book Test" },
                      { msg: "3", label: "Callback" },
                      { msg: "4", label: "Prices" },
                      { msg: "economy", label: "Economy" },
                      { msg: "CONFIRM", label: "Confirm" },
                      { msg: "A", label: "Virtual Apt" },
                    ].map((item) => (
                      <Button
                        key={item.msg}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTestMessage(item.msg);
                          if (testPhone) testJourney();
                        }}
                        className="justify-start"
                      >
                        "{item.msg}" - {item.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
